import { logger } from "@/utils/logger";
import { findDirectorByEmail, getDirectorById } from "@/services/data/directors";
import { findPersonalBrandManagerByEmail } from "@/services/data/personalBrandManagers";
import { updateBooking } from "@/services/data/bookings";
import { updateUser, getUserById } from "@/services/data/users";

/**
 * Synchronise le staff assigné par Cal.com avec nos tables locales
 * 
 * Cal.com gère le round-robin et renvoie les hosts dans la réponse de booking.
 * Cette fonction extrait ces infos et met à jour notre base de données.
 * 
 * @param calBookingData - Données de réponse de Cal.com contenant les hosts
 * @param bookingId - ID du booking dans notre DB
 * @param userId - ID de l'utilisateur
 */
export async function syncStaffFromCalBooking(
  calBookingData: any,
  bookingId: string,
  userId: string
): Promise<{
  success: boolean;
  directorId?: string;
  directorName?: string;
  directorAvatar?: string;
  pbmId?: string;
  pbmName?: string;
  pbmAvatar?: string;
  error?: any;
}> {
  try {
    // Cal.com API v2 peut retourner les données dans différentes structures
    // Essayer plusieurs chemins possibles pour trouver l'email de l'hôte
    const calData = calBookingData?.data;
    
    // Pour les événements round-robin, le host assigné peut être dans différents champs
    const hostEmail = 
      // Chemin standard Cal.com v2
      calData?.hosts?.[0]?.email ||
      // Fallback: organizer/user field (parfois utilisé pour single-host events)
      calData?.organizer?.email ||
      calData?.user?.email ||
      // Fallback: si le host est dans les attendees (2ème position, après le booker)
      calData?.attendees?.[1]?.email ||
      // Fallback: responses field (ancien format)
      calData?.responses?.email?.value ||
      null;
    
    console.log('🔍 [syncStaff] Extraction email hôte depuis Cal.com', { 
      hostEmail,
      hasHosts: !!calData?.hosts?.length,
      hostsCount: calData?.hosts?.length || 0,
      firstHost: calData?.hosts?.[0],
      organizer: calData?.organizer,
      user: calData?.user,
      attendeesCount: calData?.attendees?.length || 0,
      calDataKeys: calData ? Object.keys(calData) : []
    });
    
    if (!hostEmail) {
      logger.warn('[syncStaff] No host email found in Cal.com response', { 
        calDataKeys: calData ? Object.keys(calData) : [],
        hosts: calData?.hosts,
        organizer: calData?.organizer,
        attendees: calData?.attendees?.map((a: any) => ({ name: a?.name, email: a?.email }))
      });
      return { success: false, error: 'No host email in Cal.com response' };
    }

    console.log('🔍 [syncStaff] Syncing staff from Cal.com', { 
      hostEmail, 
      bookingId, 
      userId,
      eventSlug: calBookingData?.data?.eventType?.slug
    });

    // Déterminer si c'est un tournage (director) ou une préparation (PBM)
    // On ne reçoit ici que des bookings PRSNL : soit tournage, soit préparation.
    // Certains slugs de préparation récurring ne contiennent plus "pbm" ou "preparation"
    // (ex: "valentincuenopreprec60"), donc on considère "préparation" comme tout ce qui
    // n'est pas un tournage.
    const eventSlug = calBookingData?.data?.eventType?.slug?.toLowerCase() || '';
    const isShooting = eventSlug.includes('shooting') || eventSlug.includes('tournage');
    const isPreparation = !isShooting;

    const userResult = await getUserById(userId);
    const user = userResult.success && userResult.data ? userResult.data : null;
    const userHasAssignedDirector = !!(user?.director_id);

    let directorId: string | undefined;
    let directorName: string | undefined;
    let directorAvatar: string | undefined;
    let pbmId: string | undefined;
    let pbmName: string | undefined;
    let pbmAvatar: string | undefined;

    if (isShooting) {
      const directorResult = await findDirectorByEmail(hostEmail);

      if (!directorResult.success) {
        logger.error('[syncStaff] Error fetching director', { error: directorResult.error, hostEmail });
      } else if (directorResult.data) {
        const director = directorResult.data;
        directorId = director.id;
        directorName = `${director.first_name} ${director.last_name}`;
        directorAvatar = director.avatar_url || undefined;
        console.log('✅ [syncStaff] Director found', { 
          directorId, 
          directorName,
          directorAvatar
        });
      } else {
        console.warn('⚠️ [syncStaff] No director found with email', { hostEmail });
      }

      if (userHasAssignedDirector && user.director_id) {
        const assignedDirector = await getDirectorById(user.director_id);
        if (assignedDirector.success && assignedDirector.data) {
          const d = assignedDirector.data;
          directorId = d.id;
          directorName = `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim();
          directorAvatar = d.avatar_url ?? undefined;
        }
      }
    }

    // Récupérer le PBM si c'est une préparation
    if (isPreparation) {
      const pbmResult = await findPersonalBrandManagerByEmail(hostEmail);

      if (!pbmResult.success) {
        logger.error('[syncStaff] Error fetching PBM', { error: pbmResult.error, hostEmail });
      } else if (pbmResult.data) {
        const pbm = pbmResult.data;
        pbmId = pbm.id;
        pbmName = `${pbm.first_name} ${pbm.last_name}`;
        pbmAvatar = pbm.avatar_url || undefined;
        console.log('✅ [syncStaff] PBM found', { 
          pbmId, 
          pbmName,
          pbmAvatar 
        });
      } else {
        console.warn('⚠️ [syncStaff] No PBM found with email', { hostEmail });
      }
    }

    const bookingUpdates: any = {};
    if (isShooting && directorId && !userHasAssignedDirector) {
      bookingUpdates.director_id = directorId;
    }
    if (pbmId) bookingUpdates.personal_brand_manager_id = pbmId;

    if (Object.keys(bookingUpdates).length > 0) {
      const updateResult = await updateBooking(bookingId, bookingUpdates);

      if (!updateResult.success) {
        logger.error('[syncStaff] Error updating booking', { 
          error: updateResult.error, 
          bookingId, 
          updates: bookingUpdates 
        });
      } else {
        console.log('✅ [syncStaff] Booking updated with staff', { bookingId, updates: bookingUpdates });
      }
    }

    if (user) {
      const userUpdates: any = {};

      if (isShooting && directorId && !user.director_id) {
        userUpdates.director_id = directorId;
        if (pbmId && !user.personal_brand_manager_id) {
          userUpdates.personal_brand_manager_id = pbmId;
        }
      } else if (isPreparation && pbmId && !user.personal_brand_manager_id) {
        userUpdates.personal_brand_manager_id = pbmId;
      }

      if (Object.keys(userUpdates).length > 0) {
        const updateResult = await updateUser(userId, userUpdates);

        if (!updateResult.success) {
          logger.error('[syncStaff] Error updating user', { 
            error: updateResult.error, 
            userId, 
            updates: userUpdates 
          });
        } else {
          console.log('✅ [syncStaff] User updated with staff', { 
            userId, 
            updates: userUpdates
          });
        }
      }
    }

    const result = { 
      success: true, 
      directorId,
      directorName,
      directorAvatar,
      pbmId,
      pbmName,
      pbmAvatar
    };
    
    console.log('🎯 [syncStaff] Final result', result);
    return result;

  } catch (error) {
    logger.error('[syncStaff] Unexpected error', { error, bookingId, userId });
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
